import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { specials } from "@/mocks/specials";
import Breadcrumb from "@/components/base/Breadcrumb";
import RatingBadge from "@/components/base/RatingBadge";
import Tag from "@/components/base/Tag";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import CastList from "@/pages/detail/components/CastList";
import ReviewList from "@/pages/detail/components/ReviewList";
import ShareBar from "@/pages/detail/components/ShareBar";

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 激情视频详情介绍与精彩评论`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `查看《${movie.title}》的剧情简介、主演信息与观众评论，尽在激情视频。`,
        );
      }
    }
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-16 md:px-6">
        <EmptyState title="作品不存在或已下架" desc="你可以返回分类页继续浏览其他精彩作品。" />
        <div className="mt-6 flex justify-center">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回影视分类
          </Link>
        </div>
      </div>
    );
  }

  const special = specials.find((s) => s.id === movie.specialId);
  const related = movies
    .filter(
      (m) =>
        m.id !== movie.id &&
        (m.genres.some((g) => movie.genres.includes(g)) ||
          m.cast.some((c) => movie.cast.includes(c))),
    )
    .slice(0, 4);

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "时长 / 集数", value: movie.duration },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视分类", to: "/category" }, { label: movie.title }]} />

      <section className="relative mt-5 overflow-hidden rounded-lg">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} - 激情视频`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/85" />
        </div>
        <div className="relative flex flex-col gap-6 p-5 md:flex-row md:p-8">
          <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-lg bg-background-200 sm:mx-0 sm:w-48 md:w-56">
            <img
              src={movie.poster}
              alt={`${movie.title}海报`}
              title={`${movie.title} - 激情视频`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                {movie.type}
              </span>
              {movie.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-md border border-background-50/30 px-2.5 py-1 text-xs text-background-100"
                >
                  {t}
                </span>
              ))}
            </div>
            <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">{movie.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
              <RatingBadge value={movie.rating} />
              <span>{movie.year}</span>
              <span className="text-background-500">·</span>
              <span>{movie.region}</span>
              <span className="text-background-500">·</span>
              <span>{movie.duration}</span>
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-2 text-sm">
                  <dt className="shrink-0 text-background-400">{row.label}</dt>
                  <dd className="text-background-100">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-compass-3-line text-base" />
                探索同类作品
              </Link>
              <ShareBar title={movie.title} />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
          <i className="ri-file-text-line text-primary-500" />
          剧情简介
        </h2>
        <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">{movie.synopsis}</p>
      </section>

      <section className="mt-10">
        <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
          <i className="ri-user-star-line text-primary-500" />
          明星出演
        </h2>
        <div className="mt-4">
          <CastList castNames={movie.cast} />
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-chat-quote-line text-primary-500" />
            影视评论
          </h2>
          <span className="text-sm text-foreground-500">共 {movie.reviews.length} 条精选短评</span>
        </div>
        <div className="mt-4">
          <ReviewList reviews={movie.reviews} />
        </div>
      </section>

      {special && (
        <section className="mt-10">
          <Link
            to={`/special?id=${special.id}`}
            className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-accent-50 p-4 transition-colors hover:border-accent-300 cursor-pointer sm:flex-row sm:items-center md:p-5"
          >
            <div className="h-28 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-44">
              <img
                src={special.cover}
                alt={`${special.title}专题封面`}
                title={`${special.title} - 激情视频专题`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="min-w-0">
              <Tag tone="accent">所属专题</Tag>
              <h3 className="mt-2 text-lg font-bold text-foreground-950">{special.title}</h3>
              <p className="mt-1 text-sm text-foreground-600 line-clamp-2">{special.reason}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-secondary-600">
                查看专题
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-movie-line text-primary-500" />
            相关推荐
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}